import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLevelStore } from '../store/levelStore';
import GameCanvas from '../components/GameCanvas';
import GameOverModal from '../components/GameOverModal';
import { FiArrowLeft, FiPause, FiVolume2, FiVolumeX, FiHelpCircle, FiX } from 'react-icons/fi';
import { BsTrophy, BsLightbulb } from 'react-icons/bs';
import { soundManager } from '../engine/SoundManager';
import { GemType } from '../engine/Match3Board';

const GEM_COLORS_HEX: Record<string, string> = {
  red: '#ff4757', blue: '#3742fa', green: '#2ed573',
  yellow: '#ffa502', purple: '#8854d0', cyan: '#00d4ff', orange: '#ff6348',
};

const GamePage = () => {
  const { levelNumber } = useParams<{ levelNumber: string }>();
  const navigate = useNavigate();
  const { fetchLevel, currentLevel, submitLevelResult } = useLevelStore();

  const [score, setScore] = useState(0);
  const [moves, setMoves] = useState(0);
  const [goals, setGoals] = useState<Array<{ type: string; targetType?: string; targetCount: number; currentCount: number }>>([]);
  const [isPaused, setIsPaused] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showGameOver, setShowGameOver] = useState(false);
  const [gameResult, setGameResult] = useState<{ completed: boolean; score: number; stars: number } | null>(null);
  const [soundOn, setSoundOn] = useState(true);
  const gameEngineRef = useRef<any>(null);

  useEffect(() => {
    if (levelNumber) fetchLevel(parseInt(levelNumber));
  }, [levelNumber, fetchLevel]);

  useEffect(() => {
    if (currentLevel) {
      setScore(0);
      setMoves(currentLevel.moveLimit || 0);
      setGoals((currentLevel.goals || []).map((g: any) => ({ ...g, currentCount: 0 })));
    }
  }, [currentLevel]);

  const handleGameStart = useCallback(() => {}, []);
  const handleMovesUpdate = useCallback((m: number) => setMoves(m), []);
  const handleGoalsUpdate = useCallback((g: any[]) => setGoals(g), []);

  const handleScoreUpdate = useCallback((s: number) => {
    setScore(s);
  }, []);

  // Refs to avoid stale closures
  const currentLevelRef = useRef(currentLevel);
  currentLevelRef.current = currentLevel;
  const submitResultRef = useRef(submitLevelResult);
  submitResultRef.current = submitLevelResult;
  const scoreRef = useRef(0);
  scoreRef.current = score;
  const pendingSubmit = useRef<{ cancel: () => void }>({ cancel: () => {} });

  const handleGameOver = useCallback(async (completed: boolean, finalScore: number) => {
    const lvl = currentLevelRef.current;
    if (!lvl) return;

    let stars = 0;
    if (completed) {
      if (finalScore >= lvl.perfectScore) stars = 3;
      else if (finalScore >= lvl.goldScore) stars = 2;
      else if (finalScore >= lvl.parScore) stars = 1;
    }

    const token = { cancelled: false };
    pendingSubmit.current.cancel = () => { token.cancelled = true; };

    // Small delay to let animations finish, check if cancelled
    await new Promise(r => setTimeout(r, 200));
    if (token.cancelled) return;

    try { await submitResultRef.current(lvl.levelNumber, finalScore, completed); } catch {}

    setGameResult({ completed, score: finalScore, stars });
    setShowGameOver(true);
  }, []);

  // Auto-end when perfect score is reached during gameplay
  const autoEndTriggered = useRef(false);
  useEffect(() => {
    const lvl = currentLevelRef.current;
    if (lvl && score > 0 && score >= lvl.perfectScore && !showGameOver && !autoEndTriggered.current) {
      autoEndTriggered.current = true;
      const t = setTimeout(() => {
        handleGameOver(true, scoreRef.current);
      }, 500);
      return () => clearTimeout(t);
    }
    if (!showGameOver || !currentLevel) autoEndTriggered.current = false;
  }, [score, showGameOver, currentLevel, handleGameOver]);

  const toggleSound = () => {
    setSoundOn(!soundOn);
    soundManager.setEnabled(!soundOn);
  };

  if (!currentLevel) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#fff', fontSize: 20 }}>Loading...</div>;
  }

  // Calculate star progress
  const parTarget = currentLevel.parScore;
  const goldTarget = currentLevel.goldScore;
  const perfectTarget = currentLevel.perfectScore;
  const stars = [
    { label: '1 Звезда', target: parTarget, color: '#8888bb' },
    { label: '2 Звезды', target: goldTarget, color: '#ffaa00' },
    { label: '3 Звезды', target: perfectTarget, color: '#ff4757' },
  ];

  // Collection goals
  const collectionGoals = goals.filter(g => g.type === 'destroy');

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden', background: '#060612' }}>
      {/* Canvas fills background */}
      <GameCanvas
        level={currentLevel}
        onGameStart={handleGameStart}
        onScoreUpdate={handleScoreUpdate}
        onMovesUpdate={handleMovesUpdate}
        onGoalsUpdate={handleGoalsUpdate}
        onGameOver={handleGameOver}
        isPaused={isPaused}
        gameEngineRef={gameEngineRef}
      />

      {/* HUD overlay */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10, pointerEvents: 'none' }}>
        {/* Top bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 10px',
          background: 'linear-gradient(180deg, rgba(6,6,18,0.95) 0%, rgba(6,6,18,0.7) 80%, transparent 100%)',
          pointerEvents: 'auto'
        }}>
          {/* Left: Back + Level */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => navigate('/levels')} style={iconBtnStyle}>
              <FiArrowLeft size={18} />
            </button>
            <div>
              <div style={{ color: '#fff', fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
                Level {currentLevel.levelNumber}
              </div>
            </div>
          </div>

          {/* Center: Score + Moves */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#ffaa00', fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{score.toLocaleString()}</div>
              <div style={{ color: '#666699', fontSize: 9, textTransform: 'uppercase', letterSpacing: 1 }}>Очки</div>
            </div>
            <div style={{ width: 1, height: 30, background: '#2a2a5a' }} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: moves <= 3 ? '#ff4757' : '#00d4ff', fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{moves}</div>
              <div style={{ color: '#666699', fontSize: 9, textTransform: 'uppercase', letterSpacing: 1 }}>Ходы</div>
            </div>
          </div>

          {/* Right: Help + Sound + Pause */}
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setShowHelp(true)} style={iconBtnStyle} title="How to play">
              <FiHelpCircle size={18} />
            </button>
            <button onClick={toggleSound} style={iconBtnStyle}>
              {soundOn ? <FiVolume2 size={18} /> : <FiVolumeX size={18} />}
            </button>
            <button onClick={() => setIsPaused(true)} style={iconBtnStyle}>
              <FiPause size={18} />
            </button>
          </div>
        </div>

        {/* Star progress bars */}
        <div style={{ padding: '2px 10px 6px', pointerEvents: 'auto' }}>
          <div style={{ maxWidth: 400, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
            {stars.map((star, i) => {
              const pct = Math.min(100, Math.round((score / star.target) * 100));
              const done = score >= star.target;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    color: done ? star.color : '#555577',
                    fontSize: 10,
                    fontWeight: 700,
                    width: 48,
                    textAlign: 'right',
                    whiteSpace: 'nowrap'
                  }}>
                    {done ? '★' : '☆'} {star.label}
                  </span>
                  <div style={{
                    flex: 1, height: 8, background: '#12122a', borderRadius: 4, overflow: 'hidden',
                    border: done ? `1px solid ${star.color}` : '1px solid #1e1e3a'
                  }}>
                    <div style={{
                      width: `${pct}%`, height: '100%',
                      background: done ? star.color : `linear-gradient(90deg, ${star.color}88, ${star.color})`,
                      borderRadius: 4,
                      transition: 'width 0.3s ease',
                      opacity: done ? 1 : 0.6
                    }} />
                  </div>
                  <span style={{
                    color: done ? star.color : '#555577',
                    fontSize: 9,
                    fontWeight: 600,
                    width: 50,
                    whiteSpace: 'nowrap'
                  }}>
                    {Math.min(score, star.target)}/{star.target}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Collection goals */}
        {collectionGoals.length > 0 && (
          <div style={{ padding: '0 10px 4px', pointerEvents: 'auto' }}>
            <div style={{ maxWidth: 400, margin: '0 auto', display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
              {collectionGoals.map((g, i) => {
                const done = g.currentCount >= g.targetCount;
                const pct = Math.min(100, Math.round((g.currentCount / g.targetCount) * 100));
                const gemColor = g.targetType ? GEM_COLORS_HEX[g.targetType as GemType] ?? '#888' : '#888';
                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: done ? `${gemColor}22` : 'rgba(18,18,42,0.8)',
                    border: done ? `1px solid ${gemColor}` : '1px solid #1e1e3a',
                    borderRadius: 8, padding: '3px 8px', minWidth: 80
                  }}>
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: gemColor, opacity: done ? 1 : 0.5
                    }} />
                    <span style={{ fontSize: 9, color: done ? gemColor : '#8888bb', fontWeight: 600 }}>
                      {g.targetType} {g.currentCount}/{g.targetCount}
                    </span>
                    {done && <span style={{ fontSize: 10 }}>✓</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Help modal */}
      {showHelp && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100,
          background: 'rgba(6,6,18,0.9)', backdropFilter: 'blur(10px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
        }} onClick={() => setShowHelp(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'linear-gradient(135deg, #12123a, #1a1a4a)',
            borderRadius: 20, padding: '24px 20px', maxWidth: 420, width: '100%',
            border: '1px solid #2a2a6a', color: '#fff', position: 'relative',
            maxHeight: '90vh', overflow: 'auto'
          }}>
            <button onClick={() => setShowHelp(false)} style={{
              position: 'absolute', top: 12, right: 12, background: 'rgba(42,42,90,0.5)',
              border: 'none', borderRadius: 8, width: 32, height: 32,
              color: '#aaa', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <FiX size={18} />
            </button>

            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16, textAlign: 'center' }}>
              <BsLightbulb style={{ color: '#ffaa00', marginRight: 8 }} />
              Как играть
            </h2>

            {/* Basic rules */}
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, color: '#00d4ff', marginBottom: 6 }}>🎯 Совпадение и обмен</h3>
              <p style={{ fontSize: 12, color: '#8888bb', margin: 0, lineHeight: 1.5 }}>
                <b style={{ color: '#fff' }}>Перетащите</b> камень на соседний камень <b style={{ color: '#fff' }}>того же типа</b>, чтобы собрать их.
                Соберите <b style={{ color: '#ff4757' }}>3 или более</b>, чтобы набрать очки!
              </p>
            </div>

            {/* Star goals */}
            <div style={{ marginBottom: 16, background: 'rgba(18,18,48,0.5)', borderRadius: 12, padding: 12 }}>
              <h3 style={{ fontSize: 14, color: '#00d4ff', marginBottom: 8 }}>⭐ Цель по очкам</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {stars.map((star, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, color: star.color, fontWeight: 600 }}>{star.label}</span>
                    <span style={{ fontSize: 12, color: '#fff', fontWeight: 700 }}>{star.target.toLocaleString()} очков</span>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: 11, color: '#666699', margin: '6px 0 0' }}>
                Достигните каждой цели, чтобы заработать звёзды. Больше звёзд = лучше рейтинг!
              </p>
            </div>

            {/* Special gems */}
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, color: '#00d4ff', marginBottom: 8 }}>💎 Специальные камни</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'rgba(18,18,48,0.4)', borderRadius: 10, padding: 10 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, background: '#1a1a3a',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0
                  }}>💣</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#ff4444' }}>Бомба — 4 в ряд</div>
                    <div style={{ fontSize: 11, color: '#8888bb', lineHeight: 1.4 }}>
                      Соберите <b style={{ color: '#fff' }}>4 камня</b>, чтобы создать бомбу. Перетащите её на ближайший камень для взрыва 3×3!
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'rgba(18,18,48,0.4)', borderRadius: 10, padding: 10 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, background: '#1a1a3a',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0
                  }}>🚀</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#00ff88' }}>Ракета — L или T форма</div>
                    <div style={{ fontSize: 11, color: '#8888bb', lineHeight: 1.4 }}>
                      Соберите <b style={{ color: '#fff' }}>3+3</b> в форме <b style={{ color: '#fff' }}>L или T</b>, чтобы создать ракету. Очищает весь ряд и столбец!
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'rgba(18,18,48,0.4)', borderRadius: 10, padding: 10 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, background: '#1a1a3a',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0
                  }}>🌈</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#ffaa00' }}>Цветная бомба — 5 в ряд</div>
                    <div style={{ fontSize: 11, color: '#8888bb', lineHeight: 1.4 }}>
                      Соберите <b style={{ color: '#fff' }}>5 камней</b>, чтобы создать Цветную бомбу. Перетащите её на любой камень, чтобы уничтожить ВСЕ камни этого цвета!
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Obstacles */}
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, color: '#00d4ff', marginBottom: 8 }}>🧊 Препятствия</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#8888bb' }}>
                  <span style={{ background: '#a8d8ea33', border: '1px solid #a8d8ea', borderRadius: 6, padding: '2px 8px' }}>🧊 Лёд</span>
                  <span>— Совпадение рядом <b style={{ color: '#fff' }}>2 раза</b>, чтобы разбить</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#8888bb' }}>
                  <span style={{ background: '#888833', border: '1px solid #888', borderRadius: 6, padding: '2px 8px' }}>⛓ Цепь</span>
                  <span>— Совпадение рядом <b style={{ color: '#fff' }}>2 раза</b>, чтобы разбить</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#8888bb' }}>
                  <span style={{ background: '#8b691433', border: '1px solid #8b6914', borderRadius: 6, padding: '2px 8px' }}>📦 Ящик</span>
                  <span>— Совпадение рядом <b style={{ color: '#fff' }}>3 раза</b>, чтобы разбить</span>
                </div>
              </div>
            </div>

            <p style={{ fontSize: 11, color: '#555577', textAlign: 'center', margin: 0 }}>
              Совет: Собирайте рядом с препятствиями, чтобы повредить их. Специальные камни тоже разрушают препятствия!
            </p>
          </div>
        </div>
      )}

      {/* Pause overlay */}
      {isPaused && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 20,
          background: 'rgba(6,6,18,0.85)', backdropFilter: 'blur(10px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#12123a', borderRadius: 20, padding: '32px 48px',
            textAlign: 'center', border: '1px solid #2a2a6a'
          }}>
            <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 700, marginBottom: 24 }}>Пауза</h2>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => setIsPaused(false)} style={primaryBtnStyle}>Продолжить</button>
              <button onClick={() => navigate('/levels')} style={secondaryBtnStyle}>Выйти</button>
            </div>
          </div>
        </div>
      )}

      {/* Game over modal */}
      {showGameOver && gameResult && (
        <GameOverModal
          isOpen={showGameOver}
          result={gameResult}
          level={currentLevel}
          onNextLevel={() => {
            pendingSubmit.current.cancel();
            setShowGameOver(false);
            const lvl = currentLevelRef.current;
            if (lvl) setTimeout(() => navigate(`/game/${lvl.levelNumber + 1}`), 100);
          }}
          onReplay={() => {
            pendingSubmit.current.cancel();
            setShowGameOver(false);
            setTimeout(() => window.location.reload(), 100);
          }}
          onMenu={() => navigate('/levels')}
        />
      )}
    </div>
  );
};

const iconBtnStyle: React.CSSProperties = {
  background: 'rgba(18,18,48,0.7)', border: '1px solid #2a2a5a', borderRadius: 10,
  width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: '#aaaacc', cursor: 'pointer', backdropFilter: 'blur(8px)', padding: 0
};

const primaryBtnStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #3742fa, #00d4ff)', border: 'none', borderRadius: 12,
  padding: '12px 28px', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer'
};

const secondaryBtnStyle: React.CSSProperties = {
  background: 'transparent', border: '1px solid #2a2a5a', borderRadius: 12,
  padding: '12px 28px', color: '#aaaacc', fontSize: 16, fontWeight: 600, cursor: 'pointer'
};

export default GamePage;
